import React, { useRef, useState } from "react";
import { Upload, Paperclip, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Reusable evidence-upload widget.
 * Props:
 *   - value: current document URL (or null)
 *   - onChange: (url|null) => void
 *   - testid: string
 *   - label: string (default "Evidence")
 */
export default function EvidenceUpload({ value, onChange, testid = "evidence-upload", label = "Evidence (optional)" }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("File exceeds 5 MB limit");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.document_url);
            toast.success("Uploaded");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
    };

    return (
        <div className="space-y-1.5" data-testid={testid}>
            <div className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)]">{label}</div>
            {value ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded border bg-gray-50 text-xs"
                    style={{ borderColor: "var(--asl-border)" }}>
                    <Paperclip className="h-3.5 w-3.5 asl-navy-text" />
                    <a href={value} target="_blank" rel="noopener noreferrer" className="asl-navy-text underline truncate flex-1" data-testid={`${testid}-link`}>
                        {value.split("/").pop()}
                    </a>
                    <button type="button" onClick={() => onChange(null)} className="text-[color:var(--asl-muted)] hover:asl-crimson-text" data-testid={`${testid}-clear`}>
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : (
                <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}
                    data-testid={`${testid}-btn`} className="w-full">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Uploading…" : "Upload file (PDF / image, max 5 MB)"}
                </Button>
            )}
            <input ref={inputRef} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" hidden onChange={onPick} data-testid={`${testid}-input`} />
        </div>
    );
}
